import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  PartyPopper,
  UserCheck,
  MessageSquare,
  Images,
  Link2,
  Calendar,
  Server,
  Package,
  Ticket,
  GitBranch,
  Activity,
  Pencil,
  Trash2,
  Copy,
  Check,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { cn } from '../utils/cn';
import { eventTeamApiService } from '../services/eventTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import type { EventProjectFull, EventProjectStatus, EventTag } from '../types/eventTeam';
import { EVENT_PROJECT_STATUSES } from '../types/eventTeam';
import { getEventTeamPerms } from '../utils/eventTeamPermissions';
import { EventStatusBadge } from '../components/eventteam/EventStatusBadge';
import { EventPriorityBadge } from '../components/eventteam/EventPriorityBadge';
import { EventTagChip } from '../components/eventteam/EventTagChip';
import { EventAssignmentsSection } from '../components/eventteam/EventAssignmentsSection';
import { EventCommentsSection } from '../components/eventteam/EventCommentsSection';
import { EventGallerySection } from '../components/eventteam/EventGallerySection';
import { EventWebLinksSection } from '../components/eventteam/EventWebLinksSection';
import { EventCalendarSection } from '../components/eventteam/EventCalendarSection';
import { EventServersSection } from '../components/eventteam/EventServersSection';
import { EventModpacksSection } from '../components/eventteam/EventModpacksSection';
import { EventTicketsSection } from '../components/eventteam/EventTicketsSection';
import { EventEntityLinksSection } from '../components/eventteam/EventEntityLinksSection';
import { EventActivityLog } from '../components/eventteam/EventActivityLog';
import { EventProjectModal } from '../components/eventteam/EventProjectModal';
import { EventItemModal } from '../components/eventteam/EventItemModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreatorProfile } from '../components/CreatorProfile';
import { format, formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId =
  | 'events'
  | 'assignments'
  | 'comments'
  | 'gallery'
  | 'weblinks'
  | 'calendar'
  | 'servers'
  | 'modpacks'
  | 'tickets'
  | 'related'
  | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'events', label: 'Events', icon: PartyPopper },
  { id: 'assignments', label: 'Assignments', icon: UserCheck },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'gallery', label: 'Gallery', icon: Images },
  { id: 'weblinks', label: 'Web Links', icon: Link2 },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'servers', label: 'Servers', icon: Server },
  { id: 'modpacks', label: 'Modpacks', icon: Package },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'related', label: 'Related', icon: GitBranch },
  { id: 'activity', label: 'Activity', icon: Activity },
];

/** Project detail (guide §1.6) — the same sub-resource pattern as events. */
export const EventProjectDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  const perms = useMemo(() => getEventTeamPerms(identity, isDev), [identity, isDev]);

  const [project, setProject] = useState<EventProjectFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'events');

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);

  const [allTags, setAllTags] = useState<EventTag[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<string | null>(null);

  const loadProject = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const data = await eventTeamApiService.getProject(id);
      setProject(data);
      const uuids = new Set<string>();
      if (data.created_by) uuids.add(data.created_by);
      if (data.updated_by) uuids.add(data.updated_by);
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void loadProject();
  }, [loadProject]);

  useEffect(() => {
    eventTeamApiService.listTags().then(setAllTags).catch(() => setAllTags([]));
  }, []);

  useDocumentTitle(project ? `Project: ${project.title}` : 'Event Project');

  // Close dropdowns on outside click.
  useEffect(() => {
    const handleClick = () => {
      setShowTagPicker(false);
      setShowStatusDropdown(false);
    };
    if (showTagPicker || showStatusDropdown) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick, { once: true });
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }
  }, [showTagPicker, showStatusDropdown]);

  const handleStatusChange = async (newStatus: EventProjectStatus) => {
    if (!id) return;
    setStatusChanging(true);
    setShowStatusDropdown(false);
    try {
      let reason: string | undefined;
      if (newStatus === 'cancelled' || newStatus === 'on_hold') {
        const input = prompt(`Reason for moving to ${newStatus.replace(/_/g, ' ')} (optional):`);
        if (input === null) {
          setStatusChanging(false);
          return;
        }
        reason = input || undefined;
      }
      await eventTeamApiService.changeProjectStatus(id, newStatus, reason);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await eventTeamApiService.addTagToTarget('project', id, tagId);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await eventTeamApiService.removeTagFromTarget('project', id, tagId);
      await loadProject();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await eventTeamApiService.deleteProject(id);
      navigate('/event-team?tab=projects');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleCopyId = () => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-2">
          <SkeletonLoader className="h-3 w-24" />
          <SkeletonLoader className="h-7 w-72 max-w-full" />
          <SkeletonLoader className="h-3 w-96 max-w-full" />
          <div className="flex gap-2 pt-1">
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
        </div>
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !project || !id) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Event Team"
          title="Project"
          onBack={() => navigate('/event-team?tab=projects')}
          backLabel="Event Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load project"
          description={error || 'Project not found'}
          action={
            <>
              <button type="button" onClick={() => void loadProject()} className="glass-button">
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate('/event-team?tab=projects')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Event Team
              </button>
            </>
          }
        />
      </div>
    );
  }

  const dueStyle = (() => {
    if (!project.due_date || project.status === 'completed' || project.status === 'cancelled') return 'text-glass-400';
    if (isPast(new Date(project.due_date))) return 'text-red-400';
    if (differenceInDays(new Date(project.due_date), new Date()) <= 3) return 'text-amber-400';
    return 'text-glass-400';
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="relative z-10"
        eyebrow="Event Team"
        backTo="/event-team?tab=projects"
        backLabel="Event Team"
        title={project.title}
        titleAdornment={
          <>
            {/* Status */}
            <div className="relative flex items-center gap-1">
              <EventStatusBadge status={project.status} type="project" size="md" />
              {perms.canCreate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStatusDropdown(!showStatusDropdown);
                  }}
                  disabled={statusChanging}
                  className="btn-ghost btn-xs"
                  title="Change status"
                  aria-label="Change status"
                  aria-expanded={showStatusDropdown}
                >
                  {statusChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                  Change
                </button>
              )}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[180px] py-1">
                  {EVENT_PROJECT_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void handleStatusChange(s)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm hover:bg-glass-200/10 transition-colors',
                        s === project.status ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200',
                      )}
                    >
                      {s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <EventPriorityBadge priority={project.priority} size="md" />
            {project.category && (
              <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{project.category}</span>
            )}
          </>
        }
        description={project.description ? <span className="whitespace-pre-wrap">{project.description}</span> : undefined}
        actions={
          <>
            <button
              type="button"
              onClick={handleCopyId}
              className="glass-button-secondary"
              title="Copy ID"
              aria-label="Copy project ID"
            >
              {copiedId ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copiedId ? 'Copied' : 'ID'}
            </button>
            {perms.canCreate && (
              <button type="button" onClick={() => setIsEditOpen(true)} className="glass-button">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {perms.canAdmin && (
              <button type="button" onClick={() => setIsDeleteOpen(true)} className="btn-danger">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      >
        {/* Tags */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="eyebrow mr-1">Tags</span>
          {project.tags?.map((tag) => (
            <EventTagChip
              key={tag.id}
              tag={tag}
              onRemove={perms.canCreate ? () => void handleRemoveTag(tag.id) : undefined}
              removing={tagActionLoading === tag.id}
            />
          ))}
          {!project.tags?.length && !perms.canCreate && <span className="text-xs text-glass-500">None</span>}
          {perms.canCreate && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTagPicker(!showTagPicker);
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-glass-200/25 text-glass-400 hover:text-white hover:border-glass-200/40 transition-colors"
                aria-expanded={showTagPicker}
              >
                <Plus className="w-3 h-3" />
                Tag
              </button>
              {showTagPicker && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-20 min-w-[180px] max-h-[200px] overflow-y-auto py-1">
                  {(() => {
                    const currentTagIds = new Set(project.tags?.map((t) => t.id) || []);
                    const available = allTags.filter((t) => !currentTagIds.has(t.id));
                    if (available.length === 0) {
                      return <div className="px-3 py-2 text-xs text-glass-500">No more tags available</div>;
                    }
                    return available.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          void handleAddTag(tag.id);
                          setShowTagPicker(false);
                        }}
                        disabled={tagActionLoading === tag.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-glass-200/10 transition-colors flex items-center gap-2"
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        <span className="text-glass-200">{tag.name}</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dates + provenance */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-glass-400 tabular-nums">
          {project.due_date && (
            <span>
              Due: <span className={dueStyle}>{format(new Date(project.due_date), 'MMM d, yyyy HH:mm')}</span>
            </span>
          )}
          {project.completed_at && (
            <span>
              Completed: <span className="text-glass-200">{format(new Date(project.completed_at), 'MMM d, yyyy')}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-glass-500">
            Created {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })} by{' '}
            <CreatorProfile creatorId={project.created_by} inline />
          </span>
          {project.updated_at && project.updated_by && (
            <span className="inline-flex items-center gap-1 text-glass-500">
              Updated {formatDistanceToNow(new Date(project.updated_at), { addSuffix: true })} by{' '}
              <CreatorProfile creatorId={project.updated_by} inline />
            </span>
          )}
        </div>
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('eventProject', TABS, { events: project.events?.length ?? project.events_count, assignments: project.assignments?.filter((a) => a.status === 'active').length, comments: project.comments_count, weblinks: project.web_links?.length, servers: project.server_links?.length, modpacks: project.modpack_links?.length })} active={activeTab} onChange={setActiveTab}>


          {activeTab === 'events' && (
            <div className="space-y-3">
              <SectionHeader
                title="Events in this project"
                icon={PartyPopper}
                count={project.events?.length ?? 0}
                className="mb-0"
                actions={
                  perms.canCreate ? (
                    <button type="button" onClick={() => setShowCreateEvent(true)} className="glass-button-sm">
                      <Plus className="w-3.5 h-3.5" />
                      New event here
                    </button>
                  ) : undefined
                }
              />
              {(project.events?.length ?? 0) === 0 ? (
                <EmptyState bare compact icon={PartyPopper} title="No events in this project yet." />
              ) : (
                <div className="divide-y divide-glass-200/10">
                  {project.events.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => navigate(`/event-team/events/${event.id}`)}
                      className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-md hover:bg-glass-200/5 transition-colors text-left flex-wrap"
                    >
                      <div className="min-w-0">
                        <span className="text-sm text-white font-medium truncate block">{event.title}</span>
                        {event.scheduled_start && (
                          <span className="text-xs text-glass-400 tabular-nums">
                            {format(new Date(event.scheduled_start), 'MMM d yyyy, HH:mm')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <EventStatusBadge status={event.status} type="event" />
                        <EventPriorityBadge priority={event.priority} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === 'assignments' && (
            <EventAssignmentsSection
              targetType="project"
              targetId={id}
              perms={perms}
              onChanged={() => void loadProject()}
            />
          )}
          {activeTab === 'comments' && (
            <EventCommentsSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'gallery' && (
            <EventGallerySection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'weblinks' && (
            <EventWebLinksSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'calendar' && (
            <EventCalendarSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'servers' && (
            <EventServersSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'modpacks' && (
            <EventModpacksSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'tickets' && (
            <EventTicketsSection targetType="project" targetId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'related' && (
            <EventEntityLinksSection sourceType="project" sourceId={id} perms={perms} onChanged={() => void loadProject()} />
          )}
          {activeTab === 'activity' && <EventActivityLog source={{ kind: 'project', id }} />}

      </DetailSections>

      {/* Modals */}
      {isEditOpen && (
        <EventProjectModal
          isOpen={isEditOpen}
          mode="edit"
          project={project}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            void loadProject();
          }}
        />
      )}

      {showCreateEvent && (
        <EventItemModal
          isOpen={showCreateEvent}
          mode="create"
          projectId={id}
          onClose={() => setShowCreateEvent(false)}
          onSuccess={(created) => navigate(`/event-team/events/${created.id}`)}
        />
      )}

      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => void handleDelete()}
          title="Delete Project"
          message="Deleting removes the project; events inside it are kept but detached (project_id set to null). Consider setting the status to Cancelled instead. This action cannot be undone."
          confirmText="Delete anyway"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
